import { ReactElement } from "react"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { Modal, Tab, Tabs, Link, InlineLoading, Loading } from "@wfp/ui"
import { XLSFormSchemaByID } from "interfaces/dataInterfaces"
import useCommon from "hooks/useCommon"
import {
  useDownloadJsonSchemasByIdMutation as useDownloadJsonMFI,
  useDownloadXlsFormSchemasByIdMutation as useDownloadXlsFormMFI,
} from "services/mfi/xlsFormSchemas"
import {
  useDownloadJsonSchemasByIdMutation as useDownloadJsonRPME,
  useDownloadXlsFormSchemasByIdMutation as useDownloadXlsFormRPME,
} from "services/rpme/xlsFormSchemas"

type DetailsModalProps = {
  title?: string
  xls?: XLSFormSchemaByID
  download: ReturnType<typeof useDownloadXlsFormMFI | typeof useDownloadXlsFormRPME>[0]
  downloadJSON: ReturnType<typeof useDownloadJsonMFI | typeof useDownloadJsonRPME>[0]
  loading: boolean
  isOpen: boolean
  onClose: () => void
  module: "mfi" | "rpme"
}

const DetailsModal = ({
  title,
  xls,
  isOpen,
  onClose,
  module,
  download,
  downloadJSON,
  loading,
}: DetailsModalProps): ReactElement => {
  const { showSnackBar } = useCommon()

  const handleDownload = async (): Promise<void> => {
    if (!xls) return
    try {
      await download({ fileId: xls.xlsFormId, sourceName: xls.xlsFormSourceFile }).unwrap()
    } catch (error) {
      showSnackBar("An error occurred while downloading a required file", "error")
    }
  }

  const handleDownloadJson = async (): Promise<void> => {
    if (!xls) return
    try {
      await downloadJSON({ id: xls.xlsFormId, module }).unwrap()
    } catch (err) {
      const error = err as FetchBaseQueryError
      showSnackBar(
        (error?.data as { message: string })?.message ?? "An error occurred while downloading a required file",
        "error",
      )
    }
  }

  const RenderModalContent = (): ReactElement => {
    return (
      <div className="grid grid-cols-3">
        <div>
          <p>XLSX file:</p>
          <p>Form definition JSON:</p>
        </div>

        <div className="col-span-2">
          {xls?.xlsFormSourceFile ? (
            <p className="cursor-pointer" style={{ color: `var(--interactive-01)` }} onClick={handleDownload}>
              {xls.xlsFormSourceFile}
            </p>
          ) : (
            <p>No data available</p>
          )}

          {xls?.xlsFormId ? (
            loading ? (
              <InlineLoading />
            ) : (
              <Link onClick={handleDownloadJson}>View</Link>
            )
          ) : (
            <p>No data available</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <Modal
      open={isOpen}
      modalHeading={title || "Mapping schema details"}
      primaryButtonText="Close"
      onRequestSubmit={onClose}
      onRequestClose={onClose}
      wide
    >
      <Tabs style={{ width: "fit-content" }}>
        <Tab label="Data Export" href="" role="tab" selected tabIndex={0}>
          {loading ? <Loading small withOverlay={false} /> : <RenderModalContent />}
        </Tab>
      </Tabs>
    </Modal>
  )
}

export default DetailsModal
